import type { SelectItemType } from "@/components/base/select/select";
import {
	FORM_RULE_OPERATORS_WITHOUT_VALUE,
	FormRuleConditionOperator,
	type FormRuleConditionOperatorValue,
} from "../../types/form-rule-types";

export type ConditionValueInputKind =
	| "none"
	| "option_single"
	| "option_multi"
	| "text";

export interface FormRuleFieldOption {
	value: string;
	label: string;
}

/**
 * Determines which condition-value control to render for a field/operator pair.
 */
export function getConditionValueInputKind(
	dataType: string | undefined,
	operator: FormRuleConditionOperatorValue | undefined,
	options: FormRuleFieldOption[] | undefined,
): ConditionValueInputKind {
	if (!operator || FORM_RULE_OPERATORS_WITHOUT_VALUE.has(operator)) {
		return "none";
	}

	const hasOptions = (options?.length ?? 0) > 0;
	const isSelectOrRadio = dataType === "select" || dataType === "radio";

	if (!isSelectOrRadio || !hasOptions) {
		return "text";
	}

	if (
		dataType === "select" &&
		(operator === FormRuleConditionOperator.in ||
			operator === FormRuleConditionOperator.notIn)
	) {
		return "option_multi";
	}

	return "option_single";
}

/**
 * Coerces a stored condition value to the shape expected by the active input kind.
 */
export function normalizeConditionValue(
	value: string | number | boolean | string[] | undefined,
	kind: ConditionValueInputKind,
): string | string[] | undefined {
	switch (kind) {
		case "none":
			return undefined;
		case "option_multi": {
			if (Array.isArray(value)) {
				return value.filter(Boolean).map(String);
			}
			if (typeof value === "string" && value.length > 0) {
				return [value];
			}
			return [];
		}
		case "option_single": {
			if (Array.isArray(value)) {
				return value[0] ?? "";
			}
			if (typeof value === "string") {
				return value;
			}
			if (value === undefined || value === null) {
				return "";
			}
			return String(value);
		}
		case "text": {
			if (Array.isArray(value)) {
				return value.join(", ");
			}
			if (typeof value === "string") {
				return value;
			}
			if (value === undefined || value === null) {
				return "";
			}
			return String(value);
		}
	}
}

/**
 * Maps builder field options to MultiSelect / Select item descriptors.
 */
export function toSelectItemTypes(
	options: FormRuleFieldOption[],
): SelectItemType[] {
	return options.map((opt) => ({
		id: opt.value,
		label: opt.label,
	}));
}
